import React, {
  startTransition, useEffect, useLayoutEffect, useRef, useState,
} from "react";
import styled from "styled-components";
import { MoneyText } from "../../creditos/styles";
import { currencyParts, formatCurrency } from "../../creditos/dashboardData";

/**
 * Movimento da Reforma Tributária.
 *
 * FluidRange: o polegar responde na hora, porque só ele renderiza durante o arraste, e corre
 * de forma contínua: o valor segue a posição exata, sem passos e sem arredondar. `step` vale
 * só para as setas do teclado. O valor sobe para a tela no máximo a cada COMMIT_MS, como
 * transição, para o cálculo nunca travar o arraste. Num e AnimatedMoney: o número desliza até o novo valor escrevendo direto no DOM,
 * sem renderizar a tela a cada quadro. Barras e curvas deslizam pelo CSS de MotionScope.
 */

const COMMIT_MS = 40;
const TWEEN_MS = 420;
const EASE = "cubic-bezier(0.22, 0.8, 0.24, 1)";
const easeOut = (t) => 1 - (1 - t) ** 3;
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function useTween(value, write, element) {
  const current = useRef(value);
  const frame = useRef(0);
  const writer = useRef(write);
  writer.current = write;
  const target = useRef(null);
  target.current = element;

  useLayoutEffect(() => {
    const to = value;
    const from = current.current;
    cancelAnimationFrame(frame.current);
    if (!Number.isFinite(to) || !Number.isFinite(from) || from === to || reducedMotion()) {
      current.current = to;
      writer.current(to);
      return undefined;
    }
    const start = performance.now();
    target.current?.current?.classList.add("tw-live");
    const tick = (now) => {
      const p = Math.min(1, (now - start) / TWEEN_MS);
      current.current = p === 1 ? to : from + (to - from) * easeOut(p);
      writer.current(current.current);
      if (p < 1) frame.current = requestAnimationFrame(tick);
      else target.current?.current?.classList.remove("tw-live");
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [value]);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);
}

// Número formatado que desliza. `format` recebe sempre um número ou o valor original
export function Num({ value, format, className, ...props }) {
  const ref = useRef(null);
  const [first] = useState(() => format(value));
  useTween(typeof value === "number" ? value : NaN, (v) => {
    if (ref.current) ref.current.textContent = format(Number.isFinite(v) ? v : value);
  }, ref);
  return <span ref={ref} className={className ? `tw ${className}` : "tw"} {...props}>{first}</span>;
}

// Mesma aparência de MoneyValue, com moeda e centavos discretos
export function AnimatedMoney({ value }) {
  const integer = useRef(null);
  const decimal = useRef(null);
  const root = useRef(null);
  const [first] = useState(() => currencyParts(value));
  useTween(value || 0, (v) => {
    const parts = currencyParts(v);
    if (integer.current) integer.current.nodeValue = parts.integer;
    if (decimal.current) decimal.current.textContent = parts.decimal;
  }, root);
  const bind = (span) => { integer.current = span ? span.firstChild : null; };
  return (
    <MoneyText ref={root} className="tw" title={formatCurrency(value)}>
      <span className="cur">{first.currency}</span>
      <span ref={bind}>{first.integer}</span>
      <span className="dec" ref={decimal}>{first.decimal}</span>
    </MoneyText>
  );
}

const THUMB = 16;

// Leitura de valor contínuo: até duas casas, sem alterar o valor em si
export const finePercent = (v) => `${(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;

/**
 * Substitui o input range. `value` é o valor confirmado da tela; `format`, opcional, liga o
 * balão de valor acima do polegar durante o arraste. O traço de preenchimento lê --p do
 * próprio input, então acompanha o polegar sem esperar o cálculo.
 */
export function FluidRange({
  value, min, max, step, onChange, format, ...props
}) {
  const [local, setLocal] = useState(null);
  const [dragging, setDragging] = useState(false);
  const timer = useRef(null);
  const pending = useRef(null);
  const last = useRef(0);
  const callback = useRef(onChange);
  callback.current = onChange;

  const numeric = Number(value) || 0;
  const shown = local !== null ? local : numeric;
  const ratio = max > min ? Math.max(0, Math.min(1, (shown - min) / (max - min))) : 0;

  const flush = () => {
    clearTimeout(timer.current);
    timer.current = null;
    if (pending.current === null) return;
    const next = pending.current;
    pending.current = null;
    last.current = performance.now();
    startTransition(() => callback.current(next));
  };

  const change = (e) => {
    const next = Number(e.target.value);
    setLocal(next);
    pending.current = next;
    const wait = COMMIT_MS - (performance.now() - last.current);
    if (wait <= 0) flush();
    else if (!timer.current) timer.current = setTimeout(flush, wait);
  };

  const key = (e) => {
    const direction = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[e.key];
    if (!direction) return;
    e.preventDefault();
    const next = Math.max(min, Math.min(max, shown + direction * Number(step || 1)));
    setLocal(next);
    pending.current = next;
    flush();
  };

  const release = () => {
    flush();
    setDragging(false);
  };

  // Solta o valor local quando a tela alcança o polegar
  useEffect(() => {
    if (!dragging && local !== null && pending.current === null && numeric === local) setLocal(null);
  }, [dragging, local, numeric]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <>
      <input
        {...props}
        type="range"
        min={min}
        max={max}
        step="any"
        value={shown}
        style={{ "--p": `${ratio * 100}%` }}
        className={dragging ? "dragging" : undefined}
        onChange={change}
        onKeyDown={key}
        onPointerDown={() => setDragging(true)}
        onPointerUp={release}
        onPointerCancel={release}
        onBlur={release}
      />
      {format && (
        <span
          className={`bubble${dragging ? " on" : ""}`}
          aria-hidden="true"
          style={{ left: `calc(${THUMB / 2}px + (100% - ${THUMB}px) * ${ratio})` }}
        >
          {format(shown)}
        </span>
      )}
    </>
  );
}

// Barras, marcadores e curvas deslizam quando o valor muda, em toda a Reforma Tributária
export const MotionScope = styled.div`
  [style*="width"]:not(.bubble):not(.recharts-wrapper):not(.recharts-surface) {
    transition: width ${TWEEN_MS}ms ${EASE}, left ${TWEEN_MS}ms ${EASE}, opacity 200ms ease;
  }
  .pt, .lb, .mark { transition: left ${TWEEN_MS}ms ${EASE}; }
  .recharts-wrapper [style*="width"] { transition: none; }

  /* O número acende de leve enquanto muda, para o olho achar o que reagiu */
  .tw-live { text-shadow: 0 0 14px currentColor, 0 0 2px currentColor; }
  .tw { transition: text-shadow 260ms ease; }

  @media (prefers-reduced-motion: reduce) {
    [style*="width"], .pt, .lb, .mark { transition: none !important; }
    .tw-live { text-shadow: none; }
  }
`;
